import React from 'react';
import {
    DEFAULT_PRODUCT_LIST_DRAWER_FILTERS,
    getInvertedProductListRanges,
    pickProductListDrawerFilters,
    type ProductListDrawerFilters,
    type ProductListFilters,
    type ProductRangeFilter,
} from './components/productListFilters';

/**
 * The filters drawer (BOS-199). Its fields edit `draft`, which nothing reads
 * until Apply: the list, the address and the badge stay on the applied
 * filters while the drawer is open.
 */
export type ProductFiltersDrawerState = {
    isOpen: boolean;
    /** Copied from the applied filters on open; applied only by `onApply`. */
    draft: ProductListDrawerFilters;
    onOpen: () => void;
    /** Cancel, ×, Escape and the backdrop: the held changes are dropped. */
    onClose: () => void;
    onChange: (patch: Partial<ProductListDrawerFilters>) => void;
    /** Clears the drawer's fields, Show archived included, without applying. */
    onReset: () => void;
    /**
     * The held ranges whose minimum is above the maximum (BOS-203). Each shows
     * an inline error, and Apply stays off until there are none: the API would
     * refuse the request.
     */
    invertedRanges: ProductRangeFilter[];
    canApply: boolean;
    /** Writes the draft to the address on page 1 and closes; not while `!canApply`. */
    onApply: () => void;
};

export const useProductFiltersDrawer = (
    filters: ProductListFilters,
    applyFilters: (patch: Partial<ProductListFilters>) => void,
): ProductFiltersDrawerState => {
    // The drawer's held changes. `draft` outlives the drawer closing so its
    // fields don't blank out during the exit animation; opening copies the
    // applied filters over it again.
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<ProductListDrawerFilters>(() =>
        pickProductListDrawerFilters(filters),
    );

    const onOpenDrawer = React.useCallback(() => {
        setDraft(pickProductListDrawerFilters(filters));
        setIsDrawerOpen(true);
    }, [filters]);

    const onCloseDrawer = React.useCallback(() => setIsDrawerOpen(false), []);

    const onDraftChange = React.useCallback(
        (patch: Partial<ProductListDrawerFilters>) =>
            setDraft((current) => ({ ...current, ...patch })),
        [],
    );

    const onResetDraft = React.useCallback(
        () => setDraft(DEFAULT_PRODUCT_LIST_DRAWER_FILTERS),
        [],
    );

    const invertedRanges = React.useMemo(
        () => getInvertedProductListRanges(draft),
        [draft],
    );
    const canApply = invertedRanges.length === 0;

    const onApplyDraft = React.useCallback(() => {
        if (!canApply) {
            return;
        }
        applyFilters({ ...draft, page: 1 });
        setIsDrawerOpen(false);
    }, [applyFilters, canApply, draft]);

    return React.useMemo<ProductFiltersDrawerState>(
        () => ({
            isOpen: isDrawerOpen,
            draft,
            onOpen: onOpenDrawer,
            onClose: onCloseDrawer,
            onChange: onDraftChange,
            onReset: onResetDraft,
            invertedRanges,
            canApply,
            onApply: onApplyDraft,
        }),
        [
            canApply,
            draft,
            invertedRanges,
            isDrawerOpen,
            onApplyDraft,
            onCloseDrawer,
            onDraftChange,
            onOpenDrawer,
            onResetDraft,
        ],
    );
};
